import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { BgLandscapePng, BgPortraitPng, BonusGInfoBtn } from '../../assets';
import type { ArcadeGameType } from '../../services/arcadeApi';
import { RootState } from '../../store';
import { profileRequest } from '../../store/auth/authActions';
import { selectIsLogged } from '../../store/auth/authReducer';
import { bonusInitRequest, collectBonusRequest } from '../../store/bonus/bonusActions';
import { miniGamesInitRequest } from '../../store/miniGames';
import { env } from '../../utils/env';
import { useBonusCountdown } from '../../utils/hooks/useBonusCountdown';
import { useDevice } from '../../utils/hooks/useDevice';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import LS from '../../utils/localStorageUtils';
import { getNavPlateWidth, getNavRowHeight } from '../Components/AppFooter/Nav/FooterNavButtonsV2';
import { ArcadeGameModal } from '../Components/Arcade';
import { DESIGN_CONTENT_WIDTH, DESIGN_SECTION_GAP } from '../Components/Bonuses/bonusesTheme';
import {
	type ArcadeGame,
	FEATURED_GAMES,
	VIP_GAMES,
	VIP_GAMES_SECOND_ROW,
} from '../Components/Bonuses/bonusGamesCatalog';
import DailyBonusStrip, {
	DAILY_BONUS_DESIGN_HEIGHT,
	DAILY_BONUS_VIP_DESIGN_HEIGHT,
} from '../Components/Bonuses/DailyBonusStrip';
import FeaturedGames, { FEATURED_DESIGN_HEIGHT } from '../Components/Bonuses/FeaturedGames';
import VipBonusGames from '../Components/Bonuses/VipBonusGames';
import VipTicketBalance from '../Components/Bonuses/VipTicketBalance';
import AfterFirstFrame from '../Components/common/AfterFirstFrame';
import { RedesignScrollView } from '../Components/common/RedesignScrollView';
import { IndicatorScrollView } from '../Components/common/ScrollIndicator';
import { TouchableOpacity } from '../Components/common/Touchable';
import { useDesktopLayout } from '../Components/Desktop/desktopLayout';
import EventScrollFade from '../Components/Event/EventScrollFade';
import { PAY_TO_PLAY_MIN_LEVEL } from '../Components/Game/types';
import DailyBonusOverlayAnimation from '../Components/Modals/BonusGamesComponents/DailyBonusOverlayAnimation';
import MiniGameModal from '../Components/Modals/BonusGamesComponents/games/MiniGameModal';
import RedesignModal from '../Components/Modals/RedesignModal';
import { POPUP, POPUP_TINT, PopupDivider, PopupHeader } from '../Components/Modals/RedesignPopup';
import { useRedesignScale } from '../Components/Modals/useRedesignScale';
import { HEADER_ROW_HEIGHT } from '../Components/Top/TopSection';
import type { RootStackParamList } from '../navigator';
import { useHeaderTopOffset } from '../Pages/headerOffset';
import { MainLayout } from '../Pages/MainLayout';

/**
 * Bonuses - Figma "VIP / bonuses / arcade games" (3618:68740), page "PAVEL DRAFT", section
 * "bonuses". The frame is one 390x1118 mobile screen: the daily-bonus strip, the three featured
 * arcade cards, and the VIP grid stacked 8 apart under the header's missions / free-chips row,
 * with the navigation floating over the foot, which MainLayout supplies.
 *
 * This screen is the redesign of `BonusGamesModal`, which still ships: the footer's BONUSES
 * button opens whichever of the two `USE_BONUSES_SCREEN` selects, so they can be compared
 * side by side before the modal is retired.
 *
 * The VIP cards with an arcade game behind them run the VIP / VIP-ticket gate in `VipBonusGames`
 * and then open the game's Pixi pack in `ArcadeGameModal`; the rest render as coming soon.
 */
const DEFAULT_BONUS_AMOUNTS = [500, 750, 1000, 1250, 1500];
const DEFAULT_VIP_BONUS_AMOUNTS = [500, 750, 1000, 1250, 1500];
/** The streak the Figma strip is drawn in: day 1 collected, day 2 current. */
const DESIGN_CURRENT_DAY = 2;

// How far past the header and the nav the list keeps dimming, the same lead-in the event
// dashboard uses.
const FADE_LEAD_IN = 44;
const CONTENT_TOP_OFFSET = 12;

/** The desktop home's content block, which the desktop page fits itself into. */
const DESKTOP_BLOCK_HEIGHT = 592;

// The VIP Arcade info button, pinned to the section's top right; the ticket balance sits just
// left of it on the same line.
const MOBILE_INFO = { size: 30, top: 10, right: 6 };
const DESKTOP_INFO_RIGHT = 8;
const TICKET_BALANCE_GAP = 6;

const BonusesScreen = () => {
	const { width, height, insets } = useSafeViewport();
	const { t } = useTranslation();
	const { isDesktop } = useDevice();
	const desktop = useDesktopLayout();
	const infoModalScale = useRedesignScale({ fitHeight: 500 });
	const dispatch = useDispatch();
	const isLogged = useSelector(selectIsLogged);
	const { navigate } = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

	const headerTopOffset = useHeaderTopOffset();
	// Use the footer's exact plate width so the bonus column and bottom navigation align.
	const contentWidth = getNavPlateWidth(width, height, true);
	const scale = contentWidth / DESIGN_CONTENT_WIDTH;
	const gap = DESIGN_SECTION_GAP * scale;

	const [gamePath, setGamePath] = useState<string | null>(null);
	const [arcadeGame, setArcadeGame] = useState<ArcadeGameType | null>(null);
	const [showInfoModal, setShowInfoModal] = useState(false);
	const [levelRequiredGame, setLevelRequiredGame] = useState<ArcadeGame | null>(null);

	const requestedRef = useRef(false);
	useEffect(() => {
		if (!isLogged || requestedRef.current) {
			return;
		}
		requestedRef.current = true;
		dispatch(bonusInitRequest());
		dispatch(profileRequest());
		dispatch(miniGamesInitRequest());
	}, [dispatch, isLogged]);

	const bonus = useSelector((s: RootState) => s.bonus.bonus);
	const collecting = useSelector((s: RootState) => s.bonus.collecting);
	const miniGames = useSelector((s: RootState) => s.miniGames.games);
	const user = useSelector((s: RootState) => s.auth.user);
	const collectedAt = useSelector((s: RootState) => s.auth.user?.collectedAt) as string | undefined;
	const bonusHours = useSelector((s: RootState) => s.auth.user?.bonusHours) as string | undefined;

	const { ready: cooldownComplete } = useBonusCountdown(collectedAt, Number(bonusHours));
	const canCollect = isLogged && !!user && !collecting && (!collectedAt || cooldownComplete);

	const days = useMemo(() => {
		const amounts = Array.isArray(bonus?.bonusAmounts) ? bonus.bonusAmounts : DEFAULT_BONUS_AMOUNTS;
		const vipAmounts = Array.isArray(bonus?.vipAmounts)
			? bonus.vipAmounts
			: DEFAULT_VIP_BONUS_AMOUNTS;
		return [1, 2, 3, 4, 5].map(day => ({
			day,
			chips: amounts[day - 1] ?? 0,
			vipChips: vipAmounts[day - 1] ?? 0,
		}));
	}, [bonus]);

	// `previous_days_collected` counts the days already banked, so the next one is the current one.
	// With no streak loaded - signed out, or before the first response - the strip falls back to
	// the state the design draws it in: day 1 banked, day 2 up next, the rest locked. Showing all
	// five locked instead made the whole strip look dead.
	const currentDay = useMemo(() => {
		const collected = bonus?.previous_days_collected;
		if (collected === undefined || collected === null) {
			return DESIGN_CURRENT_DAY;
		}
		return collected >= 5 ? 1 : collected + 1;
	}, [bonus]);

	const miniGameConfig = {
		url: `${env('SVARA_API_URL')}/api/game`,
		token: LS.get('access_token') || '',
		language: LS.get('lang') || 'en',
	};

	// Signed out, every play button leads to the login screen rather than doing nothing: a
	// guest browsing the bonuses is exactly who the login prompt is for.
	const handlePlay = (launchPath: string) => {
		if (!isLogged) {
			navigate('Login');
			return;
		}
		setGamePath(
			`${launchPath}?gameApiUrl=${miniGameConfig.url}&token=${miniGameConfig.token}&language=${miniGameConfig.language}`,
		);
	};

	// The screen is hosted full-bleed: the header and nav float over the column, so the clearance at
	// both ends is this screen's to keep. The header is just its 78-high
	// row here - the missions / free-chips pills only hang under it on Home.
	const headerSolid = headerTopOffset + HEADER_ROW_HEIGHT;
	// The nav is all that floats over the foot on this route.
	const footerSolid = getNavRowHeight(width, height, true) + insets.bottom;
	// The column has to start past the whole fade, not just past the bar: content that stops
	// inside the ramp is dimmed while sitting still, which is what made the daily-bonus strip look
	// washed out before it had been scrolled at all.
	const topClearance = headerSolid + FADE_LEAD_IN + 4 * scale;
	const bottomClearance = footerSolid + gap;
	// The same backdrop MainLayout paints, so the fades match what is behind them.
	const backdrop = isDesktop ? BgLandscapePng : BgPortraitPng;
	const isVip = !!user?.isVip;
	const dailyBonusDesignHeight = isVip ? DAILY_BONUS_VIP_DESIGN_HEIGHT : DAILY_BONUS_DESIGN_HEIGHT;

	// A featured card is live when its mini-game is available to the player, or, signed out,
	// whenever it has a game behind it (the tap then opens the login screen).
	const canPlay = (game: (typeof FEATURED_GAMES)[number]) =>
		!!game.launchPath && (!isLogged || !!(game.miniGameKey && miniGames?.[game.miniGameKey]));

	const gameModal = gamePath ? (
		<MiniGameModal
			width={width}
			gamePath={gamePath}
			onClose={() => {
				setGamePath(null);
				dispatch(miniGamesInitRequest());
			}}
		/>
	) : null;
	const infoModal = showInfoModal ? (
		<RedesignModal
			scale={infoModalScale}
			onClose={() => setShowInfoModal(false)}
			panel={{ height: 500, radius: POPUP.radius, tint: POPUP_TINT, overScreen: true }}
		>
			<View
				style={[
					styles.infoModalContent,
					{
						paddingHorizontal: POPUP.paddingX * infoModalScale,
						paddingVertical: POPUP.paddingY * infoModalScale,
						rowGap: POPUP.gap * infoModalScale,
					},
				]}
			>
				<PopupHeader
					scale={infoModalScale}
					title='Bonus Games'
					onClose={() => setShowInfoModal(false)}
					closeLabel='Close bonus games rules'
				/>
				<PopupDivider scale={infoModalScale} />
				<RedesignScrollView contentContainerStyle={styles.infoModalScrollContent}>
					<View style={styles.infoModalRules}>
						<Text style={styles.infoModalText}>{t('T_SITE_BONUS_GAMES_RULES_CONTENT')}</Text>
					</View>
				</RedesignScrollView>
			</View>
		</RedesignModal>
	) : null;
	const levelRequiredModal = levelRequiredGame ? (
		<RedesignModal
			scale={infoModalScale}
			onClose={() => setLevelRequiredGame(null)}
			panel={{ height: 190, radius: POPUP.radius, tint: POPUP_TINT, overScreen: true }}
		>
			<View
				style={[
					styles.infoModalContent,
					{
						paddingHorizontal: POPUP.paddingX * infoModalScale,
						paddingVertical: POPUP.paddingY * infoModalScale,
						rowGap: POPUP.gap * infoModalScale,
					},
				]}
			>
				<PopupHeader
					scale={infoModalScale}
					title={levelRequiredGame.title}
					onClose={() => setLevelRequiredGame(null)}
					closeLabel='Close'
				/>
				<PopupDivider scale={infoModalScale} />
				<Text style={[styles.infoModalText, styles.levelRequiredText]}>
					{t('T_SITE_BONUS_GAME_LEVEL_REQUIRED', {
						defaultValue: 'Reach level {{level}} to play this game again before the timer expires.',
						level: PAY_TO_PLAY_MIN_LEVEL,
					})}
				</Text>
			</View>
		</RedesignModal>
	) : null;
	const playerLevel = user?.level || 1;

	// The VIP Arcade games (Pixi packs) open in their own host once `VipBonusGames` has passed the
	// VIP / ticket gate; the game handles the entry and not-enough-chips flow itself. A guest is sent
	// to log in.
	const openArcade = (game: ArcadeGame) => {
		if (!game.arcadeGameType) {
			return;
		}
		if (!isLogged) {
			navigate('Login');
			return;
		}
		setArcadeGame(game.arcadeGameType);
	};
	const arcadeModal = arcadeGame ? (
		<ArcadeGameModal gameType={arcadeGame} onClose={() => setArcadeGame(null)} />
	) : null;

	if (desktop.isDesktopLayout) {
		// The desktop frame uses two equal columns: the daily strip over the three featured games on
		// the left, and the VIP games on the right. The blocks keep their mobile proportions at one
		// shared width - the column's, or
		// less where the left stack (313 high per 370 wide, plus its gap) would not fit the
		// 592-high block - centred in their columns. Both sides use the same block width so their
		// visible section edges line up, rather than rendering the VIP plate narrower.
		const d = desktop.s;
		const desktopGap = d(12);
		const columnWidth = Math.max(0, (desktop.contentWidth - desktopGap) / 2);
		const columns = { left: columnWidth, right: columnWidth, gap: desktopGap };
		const gapPx = d(16);
		const blockHeight = d(DESKTOP_BLOCK_HEIGHT);
		const maxSectionHeight = Math.max(
			0,
			Math.min(blockHeight, desktop.contentHeight - d(CONTENT_TOP_OFFSET)),
		);
		const fitWidth =
			((maxSectionHeight - gapPx) * DESIGN_CONTENT_WIDTH) /
			(dailyBonusDesignHeight + FEATURED_DESIGN_HEIGHT);
		const blockWidth = Math.max(0, Math.min(columns.left, fitWidth, maxSectionHeight));
		const leftHeight =
			((dailyBonusDesignHeight + FEATURED_DESIGN_HEIGHT) * blockWidth) / DESIGN_CONTENT_WIDTH +
			gapPx;
		const sectionHeight = Math.max(leftHeight, blockWidth);
		const blockTop = Math.max(d(CONTENT_TOP_OFFSET), (desktop.contentHeight - sectionHeight) / 2);
		const vipWidth = blockWidth;
		const vipScale = vipWidth / DESIGN_CONTENT_WIDTH;
		const desktopInfoButtonSize = d(36);
		const desktopInfoButtonTop = (13 + (16 * 1.2) / 2) * vipScale - desktopInfoButtonSize / 2;

		return (
			<MainLayout fullBleed>
				<AfterFirstFrame>
					<View
						style={[
							styles.desktopContent,
							{ paddingHorizontal: desktop.side, paddingTop: blockTop },
						]}
					>
						<View style={[styles.desktopRows, { columnGap: columns.gap }]}>
							<View
								style={[
									styles.desktopColumn,
									{ width: columns.left, rowGap: gapPx, alignItems: 'flex-end' },
								]}
							>
								<DailyBonusStrip
									days={days}
									currentDay={currentDay}
									canCollect={canCollect}
									isVip={isVip}
									onCollect={() => dispatch(collectBonusRequest())}
									width={blockWidth}
								/>
								<FeaturedGames
									games={FEATURED_GAMES}
									width={blockWidth}
									canPlay={canPlay}
									onPlay={game => handlePlay(game.launchPath as string)}
									miniGames={miniGames}
									playerLevel={playerLevel}
									onLevelRequired={setLevelRequiredGame}
								/>
							</View>
							<View
								style={[styles.desktopColumn, { width: columns.right, alignItems: 'flex-start' }]}
							>
								<View style={[styles.desktopVipSection, { width: vipWidth }]}>
									<VipBonusGames
										games={VIP_GAMES}
										secondRow={VIP_GAMES_SECOND_ROW}
										width={vipWidth}
										onPlay={openArcade}
									/>
									<TouchableOpacity
										onPress={() => setShowInfoModal(true)}
										activeOpacity={0.7}
										style={[
											styles.desktopVipInfoButton,
											{
												width: desktopInfoButtonSize,
												height: desktopInfoButtonSize,
												top: desktopInfoButtonTop,
											},
										]}
									>
										<Image source={BonusGInfoBtn} style={styles.infoIcon} />
									</TouchableOpacity>
									<VipTicketBalance
										size={desktopInfoButtonSize}
										style={[
											styles.vipTicketBalance,
											{
												top: desktopInfoButtonTop,
												right: DESKTOP_INFO_RIGHT + desktopInfoButtonSize + d(TICKET_BALANCE_GAP),
											},
										]}
									/>
								</View>
							</View>
						</View>
					</View>
				</AfterFirstFrame>

				{gameModal}
				{infoModal}
				{levelRequiredModal}
				{arcadeModal}
				<DailyBonusOverlayAnimation />
			</MainLayout>
		);
	}

	return (
		<MainLayout fullBleed>
			<View style={styles.page}>
				<IndicatorScrollView
					style={styles.scroll}
					contentContainerStyle={[
						styles.scrollContent,
						{ rowGap: gap, paddingTop: topClearance, paddingBottom: bottomClearance },
					]}
					showsVerticalScrollIndicator={false}
					// The list runs under the header and footer, cleared by padding; so does the bar. Like
					// the design's full-screen lists it rides 4 in from the screen edge, in the margin the
					// centred column leaves, instead of pushing the column off centre.
					indicatorInsets={{ top: topClearance, bottom: bottomClearance, right: 4 }}
					reserveGutter={false}
				>
					<AfterFirstFrame>
						<DailyBonusStrip
							days={days}
							currentDay={currentDay}
							canCollect={canCollect}
							isVip={isVip}
							onCollect={() => dispatch(collectBonusRequest())}
							width={contentWidth}
						/>

						<FeaturedGames
							games={FEATURED_GAMES}
							width={contentWidth}
							canPlay={canPlay}
							onPlay={game => handlePlay(game.launchPath as string)}
							miniGames={miniGames}
							playerLevel={playerLevel}
							onLevelRequired={setLevelRequiredGame}
						/>

						<View style={[styles.mobileVipSection, { width: contentWidth }]}>
							<VipBonusGames
								games={VIP_GAMES}
								secondRow={VIP_GAMES_SECOND_ROW}
								width={contentWidth}
								onPlay={openArcade}
							/>
							<TouchableOpacity
								onPress={() => setShowInfoModal(true)}
								activeOpacity={0.7}
								style={styles.mobileVipInfoButton}
							>
								<Image source={BonusGInfoBtn} style={styles.infoIcon} />
							</TouchableOpacity>
							<VipTicketBalance
								size={MOBILE_INFO.size}
								style={[
									styles.vipTicketBalance,
									{
										top: MOBILE_INFO.top,
										right: MOBILE_INFO.right + MOBILE_INFO.size + TICKET_BALANCE_GAP,
									},
								]}
							/>
						</View>
					</AfterFirstFrame>
				</IndicatorScrollView>

				{/* Each fade is solid as far as its bar reaches and then eases out over a lead-in, so
				    sections dim on the way in instead of cutting off at the header's or the nav
				    plate's edge. */}
				<EventScrollFade
					edge='top'
					source={backdrop}
					viewportWidth={width}
					viewportHeight={height}
					height={headerSolid + FADE_LEAD_IN}
					solid={headerSolid}
				/>

				<EventScrollFade
					edge='bottom'
					source={backdrop}
					viewportWidth={width}
					viewportHeight={height}
					height={footerSolid + FADE_LEAD_IN}
					solid={footerSolid}
				/>
			</View>

			{infoModal}
			{levelRequiredModal}

			{gamePath ? (
				<MiniGameModal
					width={width}
					gamePath={gamePath}
					onClose={() => {
						setGamePath(null);
						dispatch(miniGamesInitRequest());
					}}
				/>
			) : null}
			{arcadeModal}
			<DailyBonusOverlayAnimation />
		</MainLayout>
	);
};

const styles = StyleSheet.create({
	page: {
		flex: 1,
		minHeight: 0,
		alignItems: 'center',
		width: '100%',
	},
	scroll: {
		flex: 1,
		minHeight: 0,
		width: '100%',
	},
	scrollContent: {
		alignItems: 'center',
	},
	desktopContent: {
		...StyleSheet.absoluteFillObject,
		alignItems: 'flex-start',
	},
	desktopRows: {
		flexDirection: 'row',
	},
	desktopVipSection: {
		position: 'relative',
	},
	desktopVipInfoButton: {
		right: DESKTOP_INFO_RIGHT,
		position: 'absolute',
		zIndex: 1,
	},
	desktopColumn: {
		alignItems: 'center',
	},
	infoIcon: {
		width: '100%',
		height: '100%',
	},
	mobileVipSection: {
		position: 'relative',
	},
	mobileVipInfoButton: {
		height: MOBILE_INFO.size,
		right: MOBILE_INFO.right,
		position: 'absolute',
		top: MOBILE_INFO.top,
		width: MOBILE_INFO.size,
		zIndex: 1,
	},
	vipTicketBalance: {
		position: 'absolute',
		zIndex: 1,
	},
	infoModalContent: {
		flex: 1,
	},
	infoModalScrollContent: {
		minHeight: 240,
	},
	infoModalRules: {
		gap: 12,
	},
	infoModalText: {
		color: '#D9C4BC',
		fontFamily: 'Sofia Sans',
		fontSize: 14,
		fontWeight: '500',
		lineHeight: 20,
	},
	levelRequiredText: {
		textAlign: 'center',
	},
});

export default BonusesScreen;
